import { useState } from "react";
import { toast } from "sonner";
import { BarChart3, Download } from "lucide-react";
import { Bar, BarChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { api, errorMessage, useApi } from "@/lib/api";
import { fmtDuration, MEDIA_LABELS } from "@/lib/format";
import type { PlaybackStats, Screen } from "@/lib/types";
import { EmptyState, Loading, PageHeader } from "@/components/app/PageHeader";

const ALL = "__all__";

function RankTable({ title, rows, testId }: { title: string; rows: { key: string; name: string; sub?: string; plays: number; seconds: number }[]; testId: string }) {
  const max = Math.max(1, ...rows.map((r) => r.plays));
  return (
    <div className="panel p-6" data-testid={testId}>
      <p className="eyebrow mb-4">{title}</p>
      <ul className="space-y-3">
        {rows.slice(0, 15).map((r) => (
          <li key={r.key} className="space-y-1">
            <div className="flex items-baseline justify-between gap-3 text-sm"><span className="truncate text-slate-200">{r.name}{r.sub && <span className="ml-2 text-xs text-slate-500">{r.sub}</span>}</span><span className="flex-none font-mono text-xs text-slate-400">{r.plays} × · {fmtDuration(r.seconds)}</span></div>
            <div className="h-1.5 rounded-full bg-slate-800"><div className="h-full rounded-full bg-orange-500" style={{ width: `${(r.plays / max) * 100}%` }} /></div>
          </li>
        ))}
        {!rows.length && <p className="text-sm text-slate-500">Aucune donnée.</p>}
      </ul>
    </div>
  );
}

export default function StatsPage() {
  const [days, setDays] = useState("7");
  const [screenId, setScreenId] = useState(ALL);
  const q = `days=${days}${screenId !== ALL ? `&screen_id=${screenId}` : ""}`;
  const { data, loading } = useApi<PlaybackStats>(`/stats/playback?${q}&tz=${encodeURIComponent(Intl.DateTimeFormat().resolvedOptions().timeZone)}`);
  const screens = useApi<Screen[]>("/screens");

  const exportCsv = async () => {
    try {
      const r = await api.get(`/stats/playback.csv?${q}`, { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url; a.download = `preuve-de-diffusion-${days}j.csv`; a.click();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error(errorMessage(e)); }
  };

  const kpis = [
    { l: "Diffusions", v: data?.totals.plays ?? 0, id: "plays" },
    { l: "Temps d'antenne", v: fmtDuration(data?.totals.seconds ?? 0), id: "seconds" },
    { l: "Écrans actifs", v: data?.totals.screens ?? 0, id: "screens" },
    { l: "Médias diffusés", v: data?.totals.media ?? 0, id: "media" },
  ];

  return (
    <div>
      <PageHeader eyebrow="Preuve de diffusion" title="Statistiques" description="Ce qui a joué, où, et combien de fois — tel que rapporté par chaque télé."
        actions={<Button data-testid="btn-export-csv" variant="outline" onClick={exportCsv} className="border-slate-700 bg-slate-800 text-slate-200 hover:bg-slate-700"><Download className="mr-1 h-4 w-4" />Exporter CSV</Button>} />
      <div className="mb-6 flex flex-wrap gap-3">
        <Select value={days} onValueChange={setDays}>
          <SelectTrigger data-testid="stats-days-select" className="w-44 border-slate-700 bg-slate-950/60"><SelectValue /></SelectTrigger>
          <SelectContent className="border-slate-800 bg-slate-900"><SelectItem value="1">Dernières 24 h</SelectItem><SelectItem value="7">7 derniers jours</SelectItem><SelectItem value="30">30 derniers jours</SelectItem><SelectItem value="90">90 derniers jours</SelectItem></SelectContent>
        </Select>
        <Select value={screenId} onValueChange={setScreenId}>
          <SelectTrigger data-testid="stats-screen-select" className="w-56 border-slate-700 bg-slate-950/60"><SelectValue /></SelectTrigger>
          <SelectContent className="border-slate-800 bg-slate-900"><SelectItem value={ALL}>Tous les écrans</SelectItem>{(screens.data || []).map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}</SelectContent>
        </Select>
      </div>
      {loading && !data ? <Loading /> : (
        <>
          <div className="mb-6 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            {kpis.map((k) => <div key={k.id} className="panel p-5"><p className="eyebrow">{k.l}</p><p data-testid={`stats-kpi-${k.id}`} className="mt-3 font-display text-3xl font-semibold text-slate-100">{k.v}</p></div>)}
          </div>
          {!data?.totals.plays ? (
            <EmptyState icon={<BarChart3 className="h-5 w-5" />} title="Aucune diffusion rapportée" text="Les télés envoient leurs rapports de diffusion après la mise à jour de l'APK (voir Formation)." testId="stats-empty" />
          ) : (
            <div className="space-y-6">
              <div className="panel p-6" data-testid="stats-chart">
                <p className="eyebrow mb-4">Diffusions par jour</p>
                <div className="h-64"><ResponsiveContainer width="100%" height="100%"><BarChart data={data.by_day}><CartesianGrid stroke="#1e293b" vertical={false} /><XAxis dataKey="date" stroke="#64748b" fontSize={11} /><YAxis stroke="#64748b" fontSize={11} allowDecimals={false} /><Tooltip contentStyle={{ background: "#0f172a", border: "1px solid #334155" }} labelStyle={{ color: "#e2e8f0" }} /><Bar dataKey="plays" name="Diffusions" fill="#f97316" radius={[4, 4, 0, 0]} /></BarChart></ResponsiveContainer></div>
              </div>
              <div className="grid gap-6 lg:grid-cols-2">
                <RankTable title="Médias les plus diffusés" testId="stats-by-media" rows={data.by_media.map((r) => ({ key: r.media_id, name: r.name, sub: r.type ? MEDIA_LABELS[r.type] : "", plays: r.plays, seconds: r.seconds }))} />
                <RankTable title="Par écran" testId="stats-by-screen" rows={data.by_screen.map((r) => ({ key: r.screen_id, name: r.name, plays: r.plays, seconds: r.seconds }))} />
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}
